<script setup lang="ts">
import { ButtonQC } from '@qicgroupdoha/qic-connect-ds';
import { BasePopup } from '@shared/ui';
import IconsIconArrowRight from '~/components/icons/IconArrowRight.vue';
import { useRtl } from '@shared/composables';
import ChevronNav from '~/components/icons/ChevronNav.vue';
import ArrowIcon from '~/libs/shared/ui/src/components/icons/ArrowIcon.vue';
import {Modal, ButtonsList, LinkItem} from '@/types/api';

const { isRtl } = useRtl();
const { locale } = useI18n();

interface Item {
  id: number;
  title: string;
  description: string;
  icon?: [
    {
      documentId: string,
      height: number,
      id: number,
      mime: string,
      size: number,
      url: string,
      width: number,
    }
  ],
  modal?: Modal;
  addon_badge?: string;
}

const props = defineProps<{
  id?: string | number;
  title: string;
  adaptive: any;
  buttons_list?: ButtonsList[];
  includes_cards: Item[];
}>();

const coveredPopupShown = ref<boolean>(false);
const currentPopupInfo = ref<Modal>({} as Modal);
const popupList = ref<Record<number, Modal>>({});
const popupIds = ref<number[]>([]);
const currentPopupIndex = ref<number>(0);
const startX = ref<number>(0);
const openItem = ref<number>(-1);
const showCoveredPopup = (id: number) => {
  currentPopupIndex.value = popupIds.value.indexOf(id);
  currentPopupInfo.value = popupList.value[id] || ({} as Modal);
  coveredPopupShown.value = true;
};
const closeCoveredPopup = () => {
  coveredPopupShown.value = false;
};
const onTouchStart = (event: TouchEvent) => {
  if (Object.keys(popupList.value).length > 1) {
    startX.value = event.touches[0].clientX;
  }
};
const onTouchEnd = (event: TouchEvent) => {
  if (Object.keys(popupList.value).length > 1) {
    const endX = event.changedTouches[0].clientX;
    const deltaX = startX.value - endX;
    if (deltaX > 50) {
      goToNextPopup();
    } else if (deltaX < -50) {
      goToPrevPopup();
    }
  }
};
const checkPopups = () => {
  for (const card of props.includes_cards) {
    if (card.modal) {
      popupList.value[card.modal.id] = card.modal;
    }
  }
  popupIds.value = Object.keys(popupList.value).map(Number);
};
const goToPrevPopup = () => {
  if (currentPopupIndex.value > 0) {
    currentPopupIndex.value -= 1;
  } else {
    currentPopupIndex.value = popupIds.value.length - 1;
  }
  showCoveredPopup(popupIds.value[currentPopupIndex.value]);
};
const goToNextPopup = () => {
  if (currentPopupIndex.value < popupIds.value.length - 1) {
    currentPopupIndex.value += 1;
  } else {
    currentPopupIndex.value = 0;
  }
  showCoveredPopup(popupIds.value[currentPopupIndex.value]);
};

onMounted(() => {
  checkPopups();
});

const toggleItem = (index: number) => {
  if (openItem.value === index) { openItem.value = -1; } else { openItem.value = index; }
};

const goToPage = (button: LinkItem) => {
  window.open(button.href, button.target);
};
</script>

<template>
  <section class="landing-includes section-offset" dir="auto">
    <div class="container">
      <h3 v-html="props.title" />
      <div class="landing-includes__wrapper" :class="{is_adaptive: adaptive}">
        <div
          v-for="item in props.includes_cards" :key="item.title" :class="[
            'landing-includes__item',
            {
              'landing-includes__item--hover': item.modal,
              'landing-includes__item--small': !item.icon,
            },
          ]" @click="item.modal ? showCoveredPopup(item.modal.id) : ''"
        >
          <div v-if="item.icon && item.icon[0]" class="landing-includes__item-icon">
            <img v-if="item.icon[0].url" :src="item.icon[0].url" alt="" loading="lazy">
          </div>
          <h4>
            {{ item.title }}
            <span v-if="item.addon_badge">{{ $t('main.includes.addon') }}</span>
          </h4>
          <p>
            {{ item.description }}
          </p>
          <button v-if="item.modal" class="landing-includes__item-link">
            {{ $t('main.includes.see_all_options.link') }}
            <IconsIconArrowRight />
          </button>
        </div>
      </div>
      <div class="landing-includes__collapse collapse">
        <div
          v-for="(item, index) in props.includes_cards" :key="item.title" class="collapse__item"
          :class="{ is_active: openItem === index }" @click="toggleItem(index)"
        >
          <div class="collapse__header">
            <ArrowIcon />
            <h4>
              {{ item.title }}
            </h4>
            <span v-if="item.addon_badge">{{ $t('main.includes.addon') }}</span>
          </div>
          <transition name="fade-collapse">
            <div v-show="openItem === index" class="collapse__body">
              <div @click.stop v-html="item.description" />
              <button
                v-if="item.modal" class="collapse__item-link"
                @click.stop="item.modal ? showCoveredPopup(item.modal.id) : ''"
              >
                {{ $t('main.includes.see_all_options.link') }}
                <IconsIconArrowRight />
              </button>
            </div>
          </transition>
        </div>
      </div>
      <div v-if="props.buttons_list" class="landing-includes__downloads">
        <div v-for="(button, index) in props.buttons_list" :key="index">
          <div v-if="button.modal" class="btn dc-link-button" @click="showCoveredPopup(button.modal.id)">
            <img v-if="button.button.icon && button.button.icon_position === 'left'" :src="button.button.icon.url">
            {{ button.button.title }}
            <img v-if="button.button.icon && button.button.icon_position === 'right'" :src="button.button.icon.url">
          </div>
          <ButtonQC
            v-else-if="button.button && button.button.href"
            :variant="'outline'"
            :icon-before-content="`file`"
            :locale="locale"
            :size="'medium'"
            @click="goToPage(button.button)"
          >
            {{ button.button.title }}
          </ButtonQC>
        </div>
      </div>
    </div>
  </section>

  <BasePopup
    v-if="coveredPopupShown" big :popup-v2="true" :is-rtl="isRtl" :use-button="true"
    @close="closeCoveredPopup"
  >
    <template #header>
      <div class="popup__header-title">
        <transition name="fade" mode="out-in">
          <h4 :key="`title_${currentPopupInfo.id}`">
            {{
              currentPopupInfo.title
            }}
          </h4>
        </transition>
        <transition name="fade" mode="out-in">
          <div :key="`subtitle_${currentPopupInfo.id}`" class="subtitle">
            {{ currentPopupInfo.description }}
          </div>
        </transition>
      </div>
      <div v-if="currentPopupInfo.image" class="popup__header-picture">
        <transition name="fade" mode="out-in">
          <img :key="`picture_${currentPopupInfo.id}`" :src="currentPopupInfo.image.url" alt="">
        </transition>
      </div>
    </template>

    <transition name="fade" mode="out-in">
      <div
        :key="`content_${currentPopupInfo.id}`" class="landing-includes__popup" @touchstart="onTouchStart"
        @touchend="onTouchEnd"
      >
        <h5>{{ currentPopupInfo.subtitle }}</h5>
        <div v-if="currentPopupInfo.content" class="content" v-html="currentPopupInfo.content" />
      </div>
    </transition>

    <template #button>
      <div class="landing-includes__popup__footer">
        <div v-if="Object.keys(popupList).length > 1" class="landing-includes__popup__nav">
          <div class="item item-prev" @click="goToPrevPopup">
            <ChevronNav />
          </div>
        </div>
        <div v-if="Object.keys(popupList).length > 1" class="landing-includes__popup__nav">
          <div class="item item-next" @click="goToNextPopup">
            <ChevronNav />
          </div>
        </div>
        <ButtonQC
          size="small"
          variant="outline"
          wide
          @click="closeCoveredPopup"
        >
          <span>{{ $t('main.includes.close.button') }}</span>
        </ButtonQC>
      </div>
    </template>
  </BasePopup>
</template>

<style lang="scss" scoped>
@import 'IncludesBlock.scss';

.rtl {
  .landing-includes__downloads{
    :deep(.ds-base-button__content) {
      flex-direction: row-reverse;
    }
  }
}
</style>
